import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { ArrowClockwise, PencilSquare, PlusLg } from "react-bootstrap-icons";
import { Link } from "react-router-dom";

import Button from "../components/layout/Button";
import LoadingSpinner from "../components/layout/LoadingSpinner";
import BulkGroupEditModal from "../components/userManagement/bulkGroupEditModal";
import ConfirmActionModal from "../components/userManagement/confirmActionModal";
import GroupBadge from "../components/userManagement/groupBadge";
import GroupFilterPanel, { GroupFilterMode } from "../components/userManagement/groupFilterPanel";
import { sortGroupNames } from "../components/userManagement/groupOrder";
import { useCurrentUsername } from "../hooks/authHooks";
import { getCachedUsers, setCachedUsers } from "../services/accountManagement.cache";
import {
  deleteUser,
  disableUser,
  enableUser,
  getUsers,
} from "../services/accountManagement.service";
import { isHttpError } from "../services/apiService";
import { UserWithGroups } from "../types/accountManagement.types";
import { isSameUser } from "../utils/isSameUser";

type SortField = "name" | "email" | "status" | "enabled";
type SortDirection = "asc" | "desc";

const compareForSort = (a: UserWithGroups, b: UserWithGroups, field: SortField): number => {
  switch (field) {
    case "name":
      return (a.name ?? "").localeCompare(b.name ?? "");
    case "email":
      return (a.email ?? "").localeCompare(b.email ?? "");
    case "status":
      return (a.status ?? "").localeCompare(b.status ?? "");
    case "enabled":
      return Number(a.enabled) - Number(b.enabled);
    default:
      return 0;
  }
};

interface SortableHeaderProps {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  field,
  label,
  sortField,
  sortDirection,
  onSort,
}) => {
  const isActive = field === sortField;
  return (
    <th
      role="button"
      tabIndex={0}
      style={{ cursor: "pointer", userSelect: "none" }}
      onClick={() => {
        onSort(field);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSort(field);
        }
      }}
    >
      {label}
      {isActive && (sortDirection === "asc" ? " ▲" : " ▼")}
    </th>
  );
};

const UsersList: React.FC = () => {
  const currentUsername = useCurrentUsername();
  const [users, setUsers] = useState<UserWithGroups[] | null>(() => getCachedUsers());
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [togglingUsername, setTogglingUsername] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>("email");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  const [groupFilterMode, setGroupFilterMode] = useState<GroupFilterMode>("or");

  const loadUsers = useCallback(async (force = false) => {
    if (force) setRefreshing(true);
    setLoadError(null);
    try {
      const { users: fetched } = await getUsers(force ? { force: true } : undefined);
      setUsers(fetched);
    } catch (error) {
      if (isHttpError(error)) {
        setLoadError(`Unable to load users (HTTP ${String(error.response.status)}).`);
      } else {
        setLoadError(
          "Unable to load users — request failed before a response was received (likely a network/CORS error). Check the browser console/network tab.",
        );
      }
    } finally {
      if (force) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const toggleSelected = (username: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(username);
      else next.delete(username);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredUsers = useMemo(() => {
    if (!users) return null;
    if (groupFilter.length === 0) return users;
    return users.filter((u) =>
      groupFilterMode === "and"
        ? groupFilter.every((g) => u.groups.includes(g))
        : groupFilter.some((g) => u.groups.includes(g)),
    );
  }, [users, groupFilter, groupFilterMode]);

  const sortedUsers = useMemo(() => {
    if (!filteredUsers) return null;
    const multiplier = sortDirection === "asc" ? 1 : -1;
    return [...filteredUsers].sort((a, b) => multiplier * compareForSort(a, b, sortField));
  }, [filteredUsers, sortField, sortDirection]);

  const toggleSelectAll = (checked: boolean) => {
    setSelected(checked ? new Set((sortedUsers ?? []).map((u) => u.username)) : new Set());
  };

  const handleConfirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteUser(deleteCandidate);
      setDeleteCandidate(null);
      await loadUsers();
    } catch (error) {
      setDeleteError(
        isHttpError(error) ? `Delete failed (${String(error.response.status)}).` : "Delete failed.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleEnabled = async (user: UserWithGroups) => {
    if (isSameUser(user.username, currentUsername)) return;
    setToggleError(null);
    setTogglingUsername(user.username);
    try {
      const updated = user.enabled
        ? await disableUser(user.username)
        : await enableUser(user.username);
      const next = (users ?? []).map((u) =>
        u.username === user.username ? { ...u, ...updated } : u,
      );
      setUsers(next);
      setCachedUsers(next);
    } catch (error) {
      setToggleError(
        isHttpError(error)
          ? `Unable to update ${user.username} (HTTP ${String(error.response.status)}).`
          : `Unable to update ${user.username}.`,
      );
    } finally {
      setTogglingUsername(null);
    }
  };

  if (users === null && !loadError) {
    return <LoadingSpinner message="Loading users..." size="medium" />;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1>User Management</h1>
        <div className="d-flex align-items-center gap-2">
          <Button type="button" disabled={refreshing} onClick={() => void loadUsers(true)}>
            <ArrowClockwise className="me-1" /> {refreshing ? "Reloading…" : "Reload Users"}
          </Button>
          <Button
            type="button"
            disabled={selected.size === 0}
            onClick={() => {
              setShowBulkEdit(true);
            }}
          >
            <PencilSquare className="me-1" /> Edit Groups ({selected.size})
          </Button>
          <Link to="/admin/users/new" className="btn">
            <PlusLg className="me-1" /> New User
          </Link>
        </div>
      </div>

      {loadError && <Alert variant="danger">{loadError}</Alert>}
      {toggleError && <Alert variant="danger">{toggleError}</Alert>}

      <GroupFilterPanel
        selectedGroups={groupFilter}
        onChange={setGroupFilter}
        mode={groupFilterMode}
        onModeChange={setGroupFilterMode}
      />

      {sortedUsers?.length === 0 && (
        <Alert variant="secondary">No users match the selected group filter.</Alert>
      )}

      {sortedUsers && sortedUsers.length > 0 && (
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selected.size === sortedUsers.length && sortedUsers.length > 0}
                  onChange={(e) => {
                    toggleSelectAll(e.target.checked);
                  }}
                  aria-label="Select all users"
                />
              </th>
              <SortableHeader
                field="name"
                label="Name"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                field="email"
                label="Email"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                field="status"
                label="Status"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                field="enabled"
                label="Enabled"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <th>Groups</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user) => (
              <tr key={user.username}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(user.username)}
                    onChange={(e) => {
                      toggleSelected(user.username, e.target.checked);
                    }}
                    aria-label={`Select ${user.username}`}
                  />
                </td>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{user.status}</td>
                <td>{user.enabled ? "Enabled" : "Disabled"}</td>
                <td>
                  {sortGroupNames(user.groups).map((group) => (
                    <GroupBadge group={group} className="me-1" key={group} />
                  ))}
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Link to={`/admin/users/${encodeURIComponent(user.username)}`} className="btn">
                      Edit
                    </Link>
                    <button
                      className={`btn ${user.enabled ? "btn-warning" : ""}`}
                      type="button"
                      disabled={
                        togglingUsername === user.username ||
                        isSameUser(user.username, currentUsername)
                      }
                      title={
                        isSameUser(user.username, currentUsername)
                          ? "You cannot disable your own account"
                          : undefined
                      }
                      onClick={() => void handleToggleEnabled(user)}
                    >
                      {togglingUsername === user.username
                        ? "Please wait…"
                        : user.enabled
                          ? "Disable"
                          : "Enable"}
                    </button>
                    <button
                      className="btn btn-danger"
                      type="button"
                      disabled={isSameUser(user.username, currentUsername)}
                      title={
                        isSameUser(user.username, currentUsername)
                          ? "You cannot delete your own account"
                          : undefined
                      }
                      onClick={() => {
                        setDeleteError(null);
                        setDeleteCandidate(user.username);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmActionModal
        show={deleteCandidate !== null}
        title="Delete User"
        body={
          <>
            <p>
              Are you sure you want to delete <strong>{deleteCandidate}</strong>? This cannot be
              undone.
            </p>
            {deleteError && <Alert variant="danger">{deleteError}</Alert>}
          </>
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        requireTypedConfirmation="confirm delete"
        busy={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => {
          setDeleteCandidate(null);
        }}
      />

      <BulkGroupEditModal
        show={showBulkEdit}
        usernames={Array.from(selected)}
        onClose={() => {
          setShowBulkEdit(false);
        }}
        onApplied={() => {
          setSelected(new Set());
          void loadUsers();
        }}
      />
    </div>
  );
};

export default UsersList;
